// Tolerant resolution of a persisted unit token to its canonical value, mirroring the Swift
// `UnitType` decode ladder in Sources/LogbookModel/Settings/ShipSettings.swift.
//
// #1331: the Ship SettingsView used to build its `<option value>` list from the generated enums'
// *keys* rather than their values, so any key≠value mismatch persisted a token the model could not
// decode — `unit.pressure` stored `hpa` where the enum's raw value is `hPa`, and the ship's whole
// sync failed. The writer is fixed, but rows written before it are still out there. Because the
// selects are uncontrolled, preselecting the canonical option is enough to repair a row: the next
// save serializes the canonical token.

// Raw values that were renamed, so they match neither a current value nor a current key. Keep in
// sync with `UnitType.legacyRawValues` on the Swift side.
export const UNIT_LEGACY_RENAMES: Record<string, string> = {
  in: "inch",
  liter: "l",
  gallon: "gal",
  kWh: "kw",
};

// Case-folded, with separators and punctuation dropped, so `square_foot`, `Square Foot` and
// `square-foot` all collapse onto the same key.
function normalize(token: string): string {
  return token.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Resolves a stored unit token to one of `options`, or returns `undefined` when nothing matches.
 *
 * Tries the exact option, then the normalized option (tolerating re-capitalization), then the
 * normalized `aliases` keys. Passing a generated enum object as `aliases` covers the key→value
 * mismatch for free, since the enum *is* that mapping.
 */
export function canonicalUnit(value: string | undefined, options: string[], aliases: Record<string, string> = {}): string | undefined {
  if (value == undefined) return undefined;
  if (options.includes(value)) return value;

  const normalized = normalize(value);
  if (normalized.length === 0) return undefined;

  const byOption = options.find((option) => normalize(option) === normalized);
  if (byOption != undefined) return byOption;

  const alias = Object.entries({ ...UNIT_LEGACY_RENAMES, ...aliases })
    .find(([key]) => normalize(key) === normalized)?.[1];
  return alias != undefined && options.includes(alias) ? alias : undefined;
}

/** `canonicalUnit` against a generated unit enum, whose keys double as the legacy-token aliases. */
export function canonicalEnumUnit(value: string | undefined, unitEnum: Record<string, string>): string | undefined {
  return canonicalUnit(value, Object.values(unitEnum), unitEnum);
}
